'use client'

import { useState } from 'react'

import { Board, CalendarUpcoming, List } from '@doist/icons/brand'
import { Box, Container, Hidden } from '@doist/marketist'

import { useTranslation } from 'app/i18n/client'
import cn from 'clsx'
import { FeatureRadioTile } from 'components/feature-radio-tile/feature-radio-tile'
import { FeatureRadioTileGroup } from 'components/feature-radio-tile/feature-radio-tile-group'
import { IconRadioTile } from 'components/icon-radio-tile/icon-radio-tile'
import { IconRadioTileGroup } from 'components/icon-radio-tile/icon-radio-tile-group'
import { ProductUIThemeProvider } from 'components/product-ui/product-ui-theme-context'
import { BoardsViewScene } from 'components/product-ui/scenes/boards-view'
import { CalendarViewScene } from 'components/product-ui/scenes/calendar-view'
import { ListViewScene } from 'components/product-ui/scenes/list-view'
import { useLocale } from 'next-intl'
import { createValueTypeGuard } from 'services/create-value-type-guard'

import css from './flexible-views-section.module.css'

const allTabs = ['list', 'calendar', 'board']

type Tab = (typeof allTabs)[number]

const typeGuard = createValueTypeGuard<Tab>(allTabs)

type FlexibleViewsSectionProps = {
    colorTheme?: 'green' | 'red' | 'seaSalt' | 'yellow'
    tabs?: Tab[]
    initialTab?: Tab
}

export function FlexibleViewsSection({
    colorTheme = 'seaSalt',
    tabs = allTabs,
    initialTab = 'list',
}: FlexibleViewsSectionProps) {
    const locale = useLocale()
    const { t } = useTranslation(locale, 'features-teams')
    const [iconGroup, setIconGroup] = useState<Tab>(initialTab)

    function onChange(value: string) {
        if (typeGuard(value)) {
            setIconGroup(value)
        }
    }

    return (
        <ProductUIThemeProvider theme={colorTheme}>
            <Hidden above="md">
                <Container width="base" className={css.tileGroupContainer} padded={true}>
                    <FeatureRadioTileGroup onChange={onChange} value={iconGroup}>
                        {tabs.includes('list') ? (
                            <FeatureRadioTile
                                title={t('list')}
                                icon={<List />}
                                value={'list'}
                                scene={
                                    <Box
                                        className={cn(css.scene, {
                                            [css.active]: iconGroup === 'list',
                                        })}
                                    >
                                        <ListViewScene />
                                    </Box>
                                }
                            />
                        ) : null}
                        {tabs.includes('calendar') ? (
                            <FeatureRadioTile
                                title={t('calendar')}
                                icon={<CalendarUpcoming />}
                                value={'calendar'}
                                scene={
                                    <Box
                                        className={cn(css.scene, {
                                            [css.active]: iconGroup === 'calendar',
                                        })}
                                    >
                                        <CalendarViewScene />
                                    </Box>
                                }
                            />
                        ) : null}
                        {tabs.includes('board') ? (
                            <FeatureRadioTile
                                title={t('board')}
                                icon={<Board />}
                                value="board"
                                scene={
                                    <Box
                                        className={cn(css.scene, {
                                            [css.active]: iconGroup === 'board',
                                        })}
                                    >
                                        <BoardsViewScene />
                                    </Box>
                                }
                            />
                        ) : null}
                    </FeatureRadioTileGroup>
                </Container>
            </Hidden>

            <Hidden below="md">
                <Container width="lg" className={css.galleryContainer} padded={false}>
                    <Box>
                        <IconRadioTileGroup value={iconGroup} onChange={onChange}>
                            {tabs.includes('list') ? (
                                <IconRadioTile icon={<List />} value="list">
                                    {t('list')}
                                </IconRadioTile>
                            ) : null}
                            {tabs.includes('calendar') ? (
                                <IconRadioTile icon={<CalendarUpcoming />} value="calendar">
                                    {t('calendar')}
                                </IconRadioTile>
                            ) : null}
                            {tabs.includes('board') ? (
                                <IconRadioTile icon={<Board />} value="board">
                                    {t('board')}
                                </IconRadioTile>
                            ) : null}
                        </IconRadioTileGroup>
                    </Box>
                    <Box className={cn(css.sceneContainer)}>
                        {tabs.includes('list') ? (
                            <Box
                                className={cn(css.scene, {
                                    [css.active]: iconGroup === 'list',
                                })}
                            >
                                <ListViewScene />
                            </Box>
                        ) : null}
                        {tabs.includes('calendar') ? (
                            <Box
                                className={cn(css.scene, {
                                    [css.active]: iconGroup === 'calendar',
                                })}
                            >
                                <CalendarViewScene />
                            </Box>
                        ) : null}
                        {tabs.includes('board') ? (
                            <Box
                                className={cn(css.scene, {
                                    [css.active]: iconGroup === 'board',
                                })}
                            >
                                <BoardsViewScene />
                            </Box>
                        ) : null}
                    </Box>
                </Container>
            </Hidden>
        </ProductUIThemeProvider>
    )
}
